import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DndContext, DragEndEvent, DragOverlay, closestCenter } from '@dnd-kit/core';
import { useApi } from '@/services/api';
import { IssuePeekOverview } from '@/components/IssuePeekOverview';
import { Plus, GripVertical } from 'lucide-react';
import clsx from 'clsx';

export function BoardPage() {
  const { projectId } = useParams<{ workspaceId: string; projectId: string }>();
  const api = useApi();
  const queryClient = useQueryClient();
  const [activeItem, setActiveItem] = useState<any>(null);
  const [isCreating, setIsCreating] = useState<string | null>(null);
  const [newItemName, setNewItemName] = useState('');
  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null);

  const { data: states } = useQuery({
    queryKey: ['states', projectId],
    queryFn: () => api.getStates(projectId!),
    enabled: !!projectId,
  });

  const { data: workItems } = useQuery({
    queryKey: ['workitems', projectId],
    queryFn: () => api.getWorkItems(projectId!),
    enabled: !!projectId,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      api.updateWorkItem(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workitems', projectId] });
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => api.createWorkItem(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workitems', projectId] });
      setIsCreating(null);
      setNewItemName('');
    },
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveItem(null);

    if (!over) return;

    const workItemId = active.id as string;
    const newStateId = over.id as string;

    // Find the work item
    const item = workItems?.find((wi: any) => wi.bh_workitemid === workItemId);
    if (!item || item._bh_state_value === newStateId) return;

    // Update the work item's state
    updateMutation.mutate({
      id: workItemId,
      data: {
        bh_state: newStateId,
      },
    });
  };

  const handleCreate = (stateId: string) => {
    if (!newItemName.trim()) return;

    createMutation.mutate({
      bh_name: newItemName.trim(),
      bh_project: projectId,
      bh_state: stateId,
    });
  };

  const getItemsForState = (stateId: string) => {
    return workItems?.filter((wi: any) => wi._bh_state_value === stateId) || [];
  };

  const getPriorityColor = (priority: number | string) => {
    const p = typeof priority === 'number' ? priority : parseInt(priority) || 1;
    switch (p) {
      case 5: return 'bg-red-500'; // urgent
      case 4: return 'bg-orange-500'; // high
      case 3: return 'bg-yellow-500'; // medium
      case 2: return 'bg-green-500'; // low
      default: return 'bg-gray-300'; // none
    }
  };

  const getPriorityLabel = (priority: number | string) => {
    const p = typeof priority === 'number' ? priority : parseInt(priority) || 1;
    switch (p) {
      case 5: return 'Urgent';
      case 4: return 'High';
      case 3: return 'Medium';
      case 2: return 'Low';
      default: return 'None';
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-gray-200 bg-white">
        <h1 className="text-xl font-semibold text-gray-900">Board View</h1>
      </div>

      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <div className="flex-1 overflow-x-auto p-4">
          <div className="flex gap-4 h-full min-w-max">
            {states?.map((state: any) => (
              <div
                key={state.bh_stateid}
                className="w-72 flex-shrink-0 flex flex-col bg-gray-100 rounded-lg"
              >
                {/* Column header */}
                <div className="p-3 flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: state.bh_color || '#6b7280' }}
                  />
                  <span className="font-medium text-gray-700">{state.bh_name}</span>
                  <span className="text-sm text-gray-400 ml-auto">
                    {getItemsForState(state.bh_stateid).length}
                  </span>
                </div>

                {/* Droppable area */}
                <div
                  className="flex-1 p-2 space-y-2 overflow-y-auto min-h-[200px]"
                  data-state={state.bh_stateid}
                >
                  {getItemsForState(state.bh_stateid).map((item: any) => (
                    <div
                      key={item.bh_workitemid}
                      draggable
                      onDragStart={() => setActiveItem(item)}
                      onClick={() => setSelectedIssueId(item.bh_workitemid)}
                      className="bg-white p-3 rounded-lg shadow-sm border border-gray-200 cursor-pointer hover:shadow-md hover:border-primary-300 transition-all group"
                    >
                      <div className="flex items-start gap-2">
                        <GripVertical className="w-4 h-4 text-gray-300 mt-0.5 opacity-0 group-hover:opacity-100 cursor-grab" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900 font-medium truncate">
                            {item.bh_name}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs text-gray-500">
                              #{item.bh_sequenceid}
                            </span>
                            {item.bh_priority && (
                              <span
                                className={clsx(
                                  'w-2 h-2 rounded-full',
                                  getPriorityColor(item.bh_priority)
                                )}
                                title={getPriorityLabel(item.bh_priority)}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Add item form */}
                  {isCreating === state.bh_stateid ? (
                    <div className="bg-white p-3 rounded-lg shadow-sm border border-primary-300">
                      <input
                        type="text"
                        value={newItemName}
                        onChange={(e) => setNewItemName(e.target.value)}
                        placeholder="What needs to be done?"
                        className="w-full text-sm border-none focus:outline-none"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleCreate(state.bh_stateid);
                          if (e.key === 'Escape') setIsCreating(null);
                        }}
                      />
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={() => handleCreate(state.bh_stateid)}
                          disabled={createMutation.isPending}
                          className="text-xs px-2 py-1 bg-primary-600 text-white rounded hover:bg-primary-700"
                        >
                          Add
                        </button>
                        <button
                          onClick={() => setIsCreating(null)}
                          className="text-xs px-2 py-1 text-gray-500 hover:bg-gray-100 rounded"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setIsCreating(state.bh_stateid)}
                      className="w-full p-2 text-sm text-gray-500 hover:bg-gray-200 rounded-lg flex items-center gap-2 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Add item
                    </button>
                  )}
                </div>
              </div>
            ))}

            {/* Add new state column */}
            {states?.length === 0 && (
              <div className="w-72 flex-shrink-0 flex flex-col items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-6">
                <p className="text-gray-500 text-center mb-4">
                  No states configured. Add states in project settings.
                </p>
              </div>
            )}
          </div>
        </div>

        <DragOverlay>
          {activeItem && (
            <div className="bg-white p-3 rounded-lg shadow-lg border border-primary-300 w-72 rotate-3">
              <p className="text-sm text-gray-900 font-medium">{activeItem.bh_name}</p>
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {/* Issue Peek Overview */}
      {selectedIssueId && (
        <IssuePeekOverview
          issueId={selectedIssueId}
          projectId={projectId!}
          onClose={() => setSelectedIssueId(null)}
        />
      )}
    </div>
  );
}
